import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, CardContent, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface ISuggestion {
    id: number;
    title: string;
    description: string;
    type: 'skill' | 'certificate' | 'role' | 'improvement';
    author: string;
    date: string;
    priority: 'low' | 'medium' | 'high';
}

export default function AdminSuggestionsWidget() {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    const [currentIndex, setCurrentIndex] = useState(0);

    // Mock-Daten für Verbesserungsvorschläge
    const suggestions: ISuggestion[] = [
        {
            id: 1,
            title: 'Neue Skill-Kategorie: Cloud Computing',
            description:
                'Vorschlag zur Erstellung einer neuen Skill-Kategorie für Cloud-Technologien wie AWS, Azure und Google Cloud.',
            type: 'skill',
            author: 'Max Mustermann',
            date: 'vor 2 Std',
            priority: 'high',
        },
        {
            id: 2,
            title: 'Zertifikat: Scrum Master',
            description:
                'Hinzufügung des Scrum Master Zertifikats zur Zertifikatsliste für bessere Projektmanagement-Abbildung.',
            type: 'certificate',
            author: 'Anna Schmidt',
            date: 'vor 1 Tag',
            priority: 'medium',
        },
        {
            id: 3,
            title: 'Rolle: DevOps Engineer',
            description:
                'Neue Rolle für DevOps Engineers mit entsprechenden Skill-Anforderungen und Verantwortlichkeiten.',
            type: 'role',
            author: 'Tom Weber',
            date: 'vor 2 Tagen',
            priority: 'medium',
        },
        {
            id: 4,
            title: 'Dashboard Verbesserung',
            description: 'Vorschlag für erweiterte Filtermöglichkeiten im Dashboard zur besseren Datenanalyse.',
            type: 'improvement',
            author: 'Lisa Klein',
            date: 'vor 3 Tagen',
            priority: 'low',
        },
    ];

    const currentSuggestion = suggestions[currentIndex];

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? suggestions.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === suggestions.length - 1 ? 0 : prev + 1));
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'skill':
                return 'primary.main';
            case 'certificate':
                return 'success.main';
            case 'role':
                return 'warning.main';
            case 'improvement':
                return 'info.main';
            default:
                return 'grey.500';
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'skill':
                return 'Skill';
            case 'certificate':
                return 'Zertifikat';
            case 'role':
                return 'Rolle';
            case 'improvement':
                return 'Verbesserung';
            default:
                return type;
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'error';
            case 'medium':
                return 'warning';
            case 'low':
                return 'success';
            default:
                return 'default';
        }
    };

    const getPriorityLabel = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'Hoch';
            case 'medium':
                return 'Mittel';
            case 'low':
                return 'Niedrig';
            default:
                return priority;
        }
    };

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.dark}20 100%)`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <LightbulbIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    Verbesserungsvorschläge
                </Typography>
                <Chip
                    label={`${currentIndex + 1}/${suggestions.length}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2 } }}>
                {suggestions.length > 0 ? (
                    <>
                        {/* Navigation */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <IconButton
                                onClick={handlePrevious}
                                disabled={suggestions.length <= 1}
                                sx={{
                                    bgcolor: theme.palette.grey[100],
                                    '&:hover': { bgcolor: theme.palette.grey[200] },
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={getTypeLabel(currentSuggestion.type)}
                                    size="small"
                                    sx={{
                                        bgcolor: getTypeColor(currentSuggestion.type) + '20',
                                        color: getTypeColor(currentSuggestion.type),
                                        fontWeight: 600,
                                    }}
                                />
                                <Chip
                                    label={getPriorityLabel(currentSuggestion.priority)}
                                    size="small"
                                    color={getPriorityColor(currentSuggestion.priority) as any}
                                    variant="outlined"
                                />
                            </Stack>

                            <IconButton
                                onClick={handleNext}
                                disabled={suggestions.length <= 1}
                                sx={{
                                    bgcolor: theme.palette.grey[100],
                                    '&:hover': { bgcolor: theme.palette.grey[200] },
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>

                        {/* Suggestion Card */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.divider}`,
                                background: `linear-gradient(135deg, ${getTypeColor(currentSuggestion.type)}08 0%, ${getTypeColor(currentSuggestion.type)}04 100%)`,
                                mb: 2,
                                flex: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                {currentSuggestion.title}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                                {currentSuggestion.description}
                            </Typography>

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Von: {currentSuggestion.author}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        {currentSuggestion.date}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                sx={{ flex: 1, textTransform: 'none' }}
                            >
                                Anzeigen
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CheckIcon />}
                                color="success"
                                sx={{ flex: 1, textTransform: 'none' }}
                            >
                                Annehmen
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon />}
                                color="error"
                                sx={{ flex: 1, textTransform: 'none' }}
                            >
                                Löschen
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                            textAlign: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <LightbulbIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="body2">Keine Verbesserungsvorschläge verfügbar</Typography>
                    </Box>
                )}
            </CardContent>
        </BaseWidget>
    );
}
